<template>
  <div>
    <aw-section-wrapper :has-padding="true" :has-title-padding="false">
      <template #content="{ contentEvents: { contentLoading } }">
        <div :class="$style.allNewsContentWrapper">
          <aw-tab-carousel
            :empty-placeholder="$awt('aw.news.list.fallback_slide.text')"
            :main-title="$awt('aw.news.list.main_title')"
            tab-key="id"
            slide-key="id"
            :tabs="tabs"
            :breakpoints="breakpoints"
            :slides="slides"
            :is-carousel="true"
            :has-title-padding="false"
            @tab-opened="getNews(contentLoading, $event.newTabItem)"
          >
            <template #slide(all)(all)="{ slide }">
              <aw-cms-card
                :key="slide.id"
                :content="{...displayTile(slide), backgroundColor: {hex: '#FAF9F8' , alpha: 1 }}"
                tile-type="verticalTile"
                :analytics-banner-interaction="analyticsBannerInteraction"
                :analytics-measurable="analyticsMeasurable"
              />
            </template>
          </aw-tab-carousel>
        </div>
        <div :class="$style.allNewsButtonWrapper">
          <lv-button styling="secondary-dark" shrink v-bind="{to: $localePath({name: 'news-type-slug', params: { type: pageType, slug: store.slug}})}">
            {{ $awt('aw.news.list.show_all_news') }}
            <lv-icon :size="16" name="arrow-right-16" style="vertical-align: text-top;" />
          </lv-button>
        </div>
      </template>
    </aw-section-wrapper>
  </div>
</template>

<script>
  import { defineAsyncComponent } from 'vue';
  import { mapState } from 'pinia';
  import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import otqs from '~~/common/utils/objectToQueryString';
  import { getBackgroundImages } from '~~/common/utils/imageHelper.js';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin.js';
  import { LvButton, LvIcon } from '~~/common/components/loginet-vue-shop/index';

  export default {
    name: 'AwNews',
    components: {
      LvIcon,
      AwCmsCard: defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwCmsCard')),
      AwTabCarousel: defineAsyncComponent(() => import('~~/common/components/AwTabCarousel')),
      AwSectionWrapper: defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper')),
      LvButton,
    },
    mixins: [analyticsBannerInteractionMixin],
    props: {
      store: {
        type: Object,
        required: true,
      },
      breakpointsOverwrite: {
        type: Array,
        default: () => ([]),
      },
      pageType: {
        type: String,
        default: '',
      },
    },
    emits: ['is-empty'],
    data () {
      const tabs = [
        {
          id: 'current',
          tabTitle: '',
        },
      ];
      return {
        slides: Object.fromEntries(tabs.map(e => [e?.id, []])),
        tabs,
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      breakpoints () {
        return this.breakpointsOverwrite.length ? this.breakpointsOverwrite : [
          {
            at: this.screenRange['desktop-medium-min'],
            count: 4,
          },
          {
            at: this.screenRange['tablet-min'],
            count: 3,
          },
          {
            at: true,
            count: 'auto',
          },
        ];
      },
    },
    methods: {
      async getNews (updateLoadingState, { id }) {
        this.selectedTabId = id;
        updateLoadingState(true);
        let params = {
          page: 1,
          limit: 12,
        };

        if (this.pageType === 'store') {
          params = {
            ...params,
            storeCode: this.store.id,
          };
        }

        if (this.pageType === 'petrol') {
          params = {
            ...params,
            petrolCode: this.store.id,
          };
        }

        const result = await this.$api.get(`cms/tag-groups/news${otqs(params)}`, { awAxCache: AW_AX_CACHE_GLOBAL });
        this.slides[id] = result.data?.contentVersions;
        updateLoadingState(false);
        this.$emit('is-empty', this.slides[id].length === 0);
      },
      displayTile (tile) {
        const images = [
          {
            ...tile.media?.desktopCardImage,
            media: tile.media?.desktopCardImage && tile.media?.mobileCardImage ? `(min-width: ${this.$styleVariables.breakpointTablet})` : undefined,
          },
          {
            ...tile.media?.mobileCardImage,
            media: tile.media?.desktopCardImage && tile.media?.mobileCardImage ? `(max-width: ${this.$styleVariables.breakpointTablet})` : undefined,
          },
        ];

        const contentImage = getBackgroundImages(this.screenRange['tablet-min'] ? images : images.reverse())[0];

        return {
          id: tile.id,
          type: 'standard',
          layoutTemplate: 'tiles',
          title: tile.title,
          contentText: tile?.lead,
          dateTag: tile.publishedAt,
          showPlaceholder: true,
          media: {
            contentImage: contentImage,
          },
          // mobileCardImage
          button: {
            alignment: 'center',
            items: [
              {
                text: this.$awt('aw.pages.aggregated_common.tile.button_title'),
                link: tile?.url,
                type: 'small-secondary-dark',
                mobileType: 'small-secondary-dark',
              },
            ],
          },
          contentBackgroundColorOnly: false,
          horizontalLayout: false,
          tags: ['csr', 'inspiration'].includes(this.pageType) ? tile.tags : [],
        };
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">

.allNews {
  &ContentWrapper {
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-x: visible;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &ButtonWrapper {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    @include tablet(min) {
      margin-top: 40px;
    }
  }
}

</style>
